import React from "react";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import "./AskAiLanding.css";

export interface LandingExample {
  title: string;
  description: string;
  prompt: string;
  icon: React.ReactNode;
}

/** FINN pages a skill card can open. Access to each is decided per team. */
export type SkillTarget =
  | "revenue-bridging"
  | "weblab-tracker"
  | "wbr-callouts";

export interface SkillAccess {
  allowed: boolean;
}

export interface LandingSkill {
  name: string;
  description: string;
  /** Page the skill opens. */
  target?: SkillTarget;
  /** Prompt the skill puts in the Ask AI composer (skills answered in chat). */
  prompt?: string;
}

export const LANDING_EXAMPLES: LandingExample[] = [
  {
    title: "Explain metric movements",
    description:
      "Run automated deep dives to find what is driving a change in any finance metric.",
    prompt: "What is causing the slowdown in US SP Search RPOM?",
    icon: <TrendingUpIcon fontSize="small" />,
  },
  {
    title: "Track weblab launches",
    description:
      "See which experiments went live in a given period and line them up with your data.",
    prompt: "Which weblabs launched in September?",
    icon: <ScienceOutlinedIcon fontSize="small" />,
  },
  {
    title: "Spot events behind the data",
    description:
      "Surface recent events and changes that could be impacting what you are seeing.",
    prompt: "What events occurred last week that could be impacting data?",
    icon: <CalendarTodayOutlinedIcon fontSize="small" />,
  },
];

export const LANDING_SKILLS: LandingSkill[] = [
  {
    name: "Revenue Bridging",
    description: "Break a revenue change down into the drivers behind it.",
    target: "revenue-bridging",
  },
  {
    name: "Metric Deep Dives",
    description: "Diagnose trends and anomalies across SPB metrics.",
    prompt:
      "Deep dive: what drove the week-over-week change in US SP revenue last week?",
  },
  {
    name: "Weblab Tracking",
    description: "Track weblab experiments and their business impact.",
    target: "weblab-tracker",
  },
  {
    name: "Event Detection",
    description: "Surface events that could be affecting your data.",
  },
  {
    name: "WBR Analysis",
    description: "Get a brief analysis ready for your weekly business review.",
    target: "wbr-callouts",
  },
];

interface AskAiLandingProps {
  isDark: boolean;
  /** Called with a prompt when an example card or a chat skill is picked. */
  onExampleSelect: (prompt: string) => void;
  /** Opens the page behind a skill. Only called for skills the team can access. */
  onSkillSelect?: (target: SkillTarget) => void;
  /** Per-target access for the current team; a missing entry counts as allowed. Skills the team can't open render as plain, non-clickable cards. */
  skillAccess?: Partial<Record<SkillTarget, SkillAccess>>;
  /** The chat composer (input, attachments, footer) rendered under the hero. */
  children: React.ReactNode;
}

/**
 * Empty-state landing page for Ask AI: hero greeting, the composer, example
 * prompts that prefill the composer, and FINN's skills. Every team sees the
 * same skill cards; the ones the team can open link to their page, the rest
 * are plain cards.
 */
const AskAiLanding: React.FC<AskAiLandingProps> = ({
  isDark,
  onExampleSelect,
  onSkillSelect,
  skillAccess,
  children,
}) => {
  const handleSkill = (skill: LandingSkill) => {
    if (skill.prompt) {
      onExampleSelect(skill.prompt);
    } else if (skill.target) {
      onSkillSelect?.(skill.target);
    }
  };

  return (
    <div className="AskAiLanding" data-dark={isDark ? "true" : "false"}>
      <section className="AskAiLanding-hero">
        <span className="AskAiLanding-badge">SPB FINANCE</span>
        <h1 className="AskAiLanding-title">Hi, I&rsquo;m FINN.</h1>
        <p className="AskAiLanding-subtitle">
          Your finance analyst, connected directly to SPB&rsquo;s data
          warehouse. Ask a question in plain language and get accurate,
          finance-grounded insight in minutes.
        </p>
      </section>

      <div className="AskAiLanding-composer">{children}</div>

      <section className="AskAiLanding-section">
        <div className="AskAiLanding-section-header">
          <h2 className="AskAiLanding-section-title">What FINN can do</h2>
          <span className="AskAiLanding-section-hint">
            Select an example to try it
          </span>
        </div>
        <div className="AskAiLanding-examples">
          {LANDING_EXAMPLES.map((example) => (
            <button
              key={example.title}
              type="button"
              className="AskAiLanding-example"
              onClick={() => onExampleSelect(example.prompt)}
            >
              <span className="AskAiLanding-example-head">
                <span className="AskAiLanding-example-icon">
                  {example.icon}
                </span>
                <span className="AskAiLanding-example-title">
                  {example.title}
                </span>
              </span>
              <span className="AskAiLanding-example-description">
                {example.description}
              </span>
              <span className="AskAiLanding-example-prompt">
                &ldquo;{example.prompt}&rdquo;
              </span>
            </button>
          ))}
        </div>
      </section>

      <section className="AskAiLanding-skills">
        <h2 className="AskAiLanding-skills-title">FINN Skills</h2>
        <p className="AskAiLanding-skills-subtitle">
          Built-in analyses, all powered by SPB&rsquo;s data warehouse.
        </p>
        <div className="AskAiLanding-skills-grid">
          {LANDING_SKILLS.map((skill) => {
            const body = (
              <>
                <span className="AskAiLanding-skill-name">{skill.name}</span>
                <span className="AskAiLanding-skill-description">
                  {skill.description}
                </span>
              </>
            );
            // Every team sees the same cards; only skills the team can open
            // are clickable. The rest stay plain cards.
            const allowed = skill.target
              ? skillAccess?.[skill.target]?.allowed !== false
              : Boolean(skill.prompt);
            if (!allowed) {
              return (
                <div
                  key={skill.name}
                  className="AskAiLanding-skill"
                  title={skill.description}
                >
                  {body}
                </div>
              );
            }
            return (
              <button
                key={skill.name}
                type="button"
                className="AskAiLanding-skill AskAiLanding-skill-link"
                title={skill.description}
                onClick={() => handleSkill(skill)}
              >
                {body}
              </button>
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default AskAiLanding;
